import { useState } from "react";
import { DataTable } from "@/components/data-tables/data-table";
import { createChatProviderColumns, ChatProviderSearchableColumns } from "./ChatProviderDataTableColumns";
import { useDataTable } from "@/hooks/useDataTable";
import { Button } from "@/components/ui/button";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import {
    useDeleteChatProviderMutation,
    useSetEnabledChatProviderMutation,
} from "@/api/mutations/admin/ChatProviderMutations";
import { toast } from "sonner";
// Removed formatProviderName import since we now use display_name directly
import { ChatProviderDialog } from "@/components/dialogs/providers/ChatProviderDialog";
import { Provider } from "@shared/validators/admin/settings-validators";
import { useTranslation } from "react-i18next";

interface ChatProviderActions {
    onViewDetails?: (provider: Provider) => void;
    onEdit?: (provider: Provider) => void;
    onToggleEnabled?: (provider: Provider) => void;
    onDuplicate?: (provider: Provider) => void;
}

type ChatProviderDataTableProps = {
    data: Provider[];
    actions?: ChatProviderActions;
};

const ChatProviderDataTable = ({ data, actions }: ChatProviderDataTableProps) => {
    const { t } = useTranslation();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
    const [duplicateProvider, setDuplicateProvider] = useState<Provider | null>(null);

    const setEnabledChatProviderMutation = useSetEnabledChatProviderMutation();

    // Default action callbacks
    const defaultActions: ChatProviderActions = {
        onViewDetails: (provider) => {
            toast.info(t("dataTables.viewingDetailsFor", { name: provider.display_name }));
            // TODO: Implement view details modal
        },
        onEdit: (provider) => {
            setSelectedProvider(provider);
            setEditDialogOpen(true);
        },
        onToggleEnabled: (provider) => {
            setEnabledChatProviderMutation.mutate({
                id: provider.id,
                enabled: !provider.enabled,
            });
        },
        onDuplicate: (provider) => {
            // Set the provider to duplicate and open create dialog
            setDuplicateProvider(provider);
            setCreateDialogOpen(true);
        },
    };

    const finalActions = actions || defaultActions;
    const columns = createChatProviderColumns(finalActions);

    const { table } = useDataTable({
        columns,
        data,
        pageCount: 1,
        searchableColumns: ChatProviderSearchableColumns,
    });

    const rows = table.getSelectedRowModel();
    const ids = rows.flatRows.map((row) => row.original.id) as string[];
    const { mutate } = useDeleteChatProviderMutation();

    const handleDeleteProviders = () => {
        if (confirm(t("dataTables.confirmDeleteModels"))) {
            ids.forEach((id) => mutate({ id }));
        }
    };

    const handleCreateProvider = () => {
        setSelectedProvider(null); // Clear any selected provider for fresh creation
        setDuplicateProvider(null); // Clear any duplicate provider
        setCreateDialogOpen(true);
    };

    return (
        <>
            <DataTable
                table={table}
                columns={columns}
                searchableColumns={ChatProviderSearchableColumns}
                newRowButton={
                    <Button variant="outline" size="sm" className="h-8" onClick={handleCreateProvider}>
                        <PlusCircledIcon className="mr-2 size-4" aria-hidden="true" />
                        {t("dataTables.newModel")}
                    </Button>
                }
                deleteRowsAction={handleDeleteProviders}
            />

            {/* Create Provider Dialog */}
            <ChatProviderDialog 
                open={createDialogOpen} 
                onOpenChange={(open) => {
                    setCreateDialogOpen(open);
                    if (!open) {
                        // Clear duplicate provider when dialog closes
                        setDuplicateProvider(null);
                    }
                }} 
                provider={duplicateProvider || selectedProvider} 
                mode="create" 
            />

            {/* Edit Provider Dialog */}
            <ChatProviderDialog
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                provider={selectedProvider}
                mode="edit"
            />
        </>
    );
};

export default ChatProviderDataTable;
